import axios from 'axios';
import { OrderModel, OrderStateEnum } from '../models/OrderModel';

const API_URL = process.env.REACT_APP_API_URL;
const ORDERS_URL = `${API_URL}/api/b2b/orders/`;
export const ORDERS_PER_PAGE = 20;

interface ListOrdersProps {
  state?: Array<OrderStateEnum>;
  distributorId?: number;
  page?: number;
  country?: string;
  origin?: string;
  promiseDateSince?: string;
  promiseDateUntil?: string;
  preparationDateSince?: string;
  preparationDateUntil?: string;
  invoiceDateSince?: string;
  invoiceDateUntil?: string;
  code?: string;
  externalReference?: string;
  shippingService?: string;
  warehouse?: number;
  onlyFreeOrders?: boolean;
  shippingAddressId?: number;
}

interface OrderListResponse {
  count: number;
  next: string;
  previous: string;
  results: Array<OrderModel>;
}
export function listOrders({
  state,
  distributorId,
  page,
  country,
  origin,
  promiseDateSince,
  promiseDateUntil,
  preparationDateSince,
  preparationDateUntil,
  invoiceDateSince,
  invoiceDateUntil,
  code,
  externalReference,
  shippingService,
  warehouse,
  onlyFreeOrders,
  shippingAddressId,
}: ListOrdersProps): Promise<{ data: OrderListResponse }> {
  const limit = ORDERS_PER_PAGE;
  const offset = (page - 1) * limit;
  const params: any = {
    distributor: distributorId,
    limit,
    offset,
    country,
    origin: origin || undefined,
    promise_date__gte: promiseDateSince,
    promise_date__lte: promiseDateUntil,
    preparation_date__gte: preparationDateSince,
    preparation_date__lte: preparationDateUntil,
    invoice_date__gte: invoiceDateSince,
    invoice_date__lte: invoiceDateUntil,
    code,
    external_reference: externalReference,
    shipping_service: shippingService,
    warehouse: warehouse ?? undefined,
    only_free_orders: onlyFreeOrders || undefined,
    shipping_address: shippingAddressId ?? undefined,
  };
  if (state?.length) {
    params.state = state.join(',');
  }
  return axios.get(ORDERS_URL, { params });
}

export function retrieveOrder(id: number): Promise<{ data: OrderModel }> {
  return axios.get(`${ORDERS_URL}${id}/`);
}

export function createOrder(order: OrderModel): Promise<{ data: OrderModel }> {
  return axios.post(ORDERS_URL, order);
}

export function createOrderFromEdi(
  distributorId: number,
  edi: string
): Promise<{ data: OrderModel }> {
  return axios.post(`${ORDERS_URL}process-edi/`, {
    content: edi,
    distributor: distributorId,
  });
}

export function updateOrder(order: OrderModel): Promise<{ data: OrderModel }> {
  const {
    distributor,
    promise_date,
    shipping_fee,
    total_discounts,
    shipping_address,
    billing_address,
    shipping_service,
    state,
    comments,
    external_reference,
    discount_mode,
    discount_on,
  } = order;

  return axios.patch(`${ORDERS_URL}${order.id}/`, {
    distributor,
    promise_date,
    shipping_fee,
    total_discounts,
    shipping_address,
    billing_address,
    state,
    comments,
    external_reference,
    discount_mode,
    discount_on,
    shipping_service,
  });
}

export enum BulkActionsEnum {
  GENERATE_WAREHOUSE_OPERATIONS_SHEET = 'generate_warehouse_operations_sheet',
  PRINT_TAGS = 'print_tags',
  PREPARE_ORDERS = 'prepare_orders',
  GENERATE_PRODUCTS_NECESSITY_SHEET = 'generate_products_necessity_sheet',
  EXPORT_ORDERS = 'export_orders',
  EXPORT_COMERCIAL_ORDERS = 'export_comercial_orders',
  DOWNLOAD_RUTERO_FILE = 'download_rutero_file',
}

interface BulkActionProps {
  orders: Array<number>;
  action: BulkActionsEnum;
  country?: string;
  origin?: string;
  promiseDateSince?: string;
  promiseDateUntil?: string;
  preparationDateSince?: string;
  preparationDateUntil?: string;
  invoiceDateSince?: string;
  invoiceDateUntil?: string;
  code?: string;
  externalReference?: string;
  distributor?: number;
  state?: string;
  shippingService?: string;
  warehouse?: number;
  onlyFreeOrders?: boolean;
}

export function performBulkAction({
  orders,
  action,
  country,
  origin,
  promiseDateSince,
  promiseDateUntil,
  preparationDateSince,
  preparationDateUntil,
  invoiceDateSince,
  invoiceDateUntil,
  code,
  externalReference,
  distributor,
  state,
  shippingService,
  warehouse,
  onlyFreeOrders,
}: BulkActionProps) {
  const endpoint = `${API_URL}/api/b2b/orders/massive-actions/`;

  return axios.post(endpoint, {
    action,
    orders,
    country,
    origin: origin || undefined,
    promise_date__gte: promiseDateSince,
    promise_date__lte: promiseDateUntil,
    preparation_date__gte: preparationDateSince,
    preparation_date__lte: preparationDateUntil,
    invoice_date__gte: invoiceDateSince,
    invoice_date__lte: invoiceDateUntil,
    code,
    external_reference: externalReference,
    distributor,
    state,
    shipping_service: shippingService,
    warehouse: warehouse ?? undefined,
    only_free_orders: onlyFreeOrders || undefined,
  });
}

interface BillingReport {
  subtotal_by_days: any;
  total_subtotal: number;
}
interface GetBillingReportProps {
  since: string;
  until: string;
  countries?: string[];
}
export function getBillingReport({
  since,
  until,
  countries,
}: GetBillingReportProps): Promise<{ data: BillingReport }> {
  const endpoint = `${API_URL}/api/b2b/orders/reports/billing/`;
  const params = {
    since_data: since,
    until_data: until,
    country: countries?.length ? countries.join(',') : undefined,
  };

  return axios.get(endpoint, { params });
}

export function getBillingReportCsv({
  since,
  until,
  countries,
}: GetBillingReportProps): Promise<any> {
  const endpoint = `${API_URL}/api/b2b/orders/reports/csv-billing/`;
  const params = {
    since_data: since,
    until_data: until,
    country: countries?.length ? countries.join(',') : undefined,
  };

  return axios.get(endpoint, { params });
}

interface EuroKgResponse {
  total_euro_kg: number;
}
export function getEuroKg({
  since,
  until,
  countries,
}: GetBillingReportProps): Promise<{ data: EuroKgResponse }> {
  const endpoint = `${API_URL}/api/b2b/orders/reports/euro-kg/`;
  const params = {
    since_data: since,
    until_data: until,
    country: countries?.length ? countries.join(',') : undefined,
  };
  return axios.get(endpoint, { params });
}

export interface SalesReportVariant {
  variant_id: number;
  variant_name: string;
  total_units: number;
  total_kg: number;
}

export interface SalesReportRecipe {
  recipe_id: number;
  recipe_name: string;
  total_kg: number;
}

export interface SalesReport {
  by_variant: SalesReportVariant[];
  by_recipe: SalesReportRecipe[];
}

interface GetSalesReportProps extends GetBillingReportProps {
  origin?: string;
}

export function getSalesReport({
  since,
  until,
  origin,
}: GetSalesReportProps): Promise<{ data: SalesReport }> {
  const endpoint = `${API_URL}/api/b2b/orders/reports/sales/`;
  const params = {
    since_data: since,
    until_data: until,
    origin: origin || undefined,
  };
  return axios.get(endpoint, { params });
}

interface ListAvailableDeliveryDatesProps {
  distributorId: number;
  shippingServiceId: number;
  shippingAddressId: number;
  // Order being edited: its own day stays available even when full.
  orderId?: number;
}
export function listAvailableDeliveryDates({
  distributorId,
  shippingServiceId,
  shippingAddressId,
  orderId,
}: ListAvailableDeliveryDatesProps): Promise<{ data: Array<string> }> {
  const endpoint = `${API_URL}/api/b2b/orders/available-delivery-dates/`;
  const params = {
    distributor: distributorId,
    shipping_service: shippingServiceId,
    shipping_address: shippingAddressId,
    order: orderId,
  };

  return axios.get(endpoint, { params });
}
